import React, { useMemo } from "react";
import Grid from "@material-ui/core/Grid";
import { PrimaryButton, SecondaryButton } from "@prestosports/commons-ui";
import { ParticipationStyle } from "@prestosports/prestostats-core/Football";
import { ImportService } from "@prestosports/prestostats-core/Services";
import Divider from "@material-ui/core/Divider";
import Toolbar from "@material-ui/core/Toolbar";
import * as Yup from "yup";
import { useFormik } from "formik";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import gameFinalizeStyles from "../../team/styles/GameFinalize.styles";
import commonStyles from "../../common/styles/Common.styles";
import TeamRecords from "./TeamRecords";
import { getSafeOrDefault } from "../../../util/object-util";
import FormMessages from "../../errors/components/FormMessages";
import ClockTimePicker from "../../common/components/ClockTimePicker";
import { useAppContext } from "../../../state/app/context";
import { GameRoutes } from "../../../routes/game-routes";
import ParticipantConfirmationList from "./ParticipantConfirmationList";

const GameFinalize = ({
	awayTeam,
	homeTeam,
	settings,
	event,
	onFinalize,
	onSave,
	onCancel,
	currentEventSettings,
	awayParticipatingPlayers,
	homeParticipatingPlayers
}) => {
	const classes = gameFinalizeStyles();
	const commonClasses = commonStyles();

	const { participationStyle } =
		currentEventSettings?.gameConfiguration?.general || {};

	const { state: appState, redirect } = useAppContext();
	const isSubmitting = appState.isSubmitting();

	const validationSchema = Yup.object({
		attendance: Yup.number().min(
			0,
			"Attendance must be greater than or equal to zero"
		)
	});

	const defaultEndTime = useMemo(() => new Date().toISOString(), []);

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			attendance: "",
			endTime: defaultEndTime,
			notes: event.notes,
			records: {
				[awayTeam.id]: { overall: "", conference: "" },
				[homeTeam.id]: { overall: "", conference: "" }
			},
			...settings
		},
		validationSchema,
		onSubmit: (values) => {
			onFinalize(values);
		}
	});

	const {
		values,
		touched,
		handleSubmit,
		handleChange,
		isValid,
		setFieldValue,
		errors
	} = formik;

	const handleSave = () => {
		onSave(values);
	};

	const setEndTime = (value) => {
		setFieldValue("endTime", value);
	};

	const handleCancel = () => {
		if (!isSubmitting) onCancel();
	};

	const onModifyParticipants = (teamId) => {
		const url =
			teamId === awayTeam.id
				? GameRoutes.TEAM_INFORMATION_VISITOR.url
				: GameRoutes.TEAM_INFORMATION_HOME.url;
		redirect(`${url.replace(":eventId", event.id)}#roster`);
	};

	const getCsvContent = (teamId) => {
		const players =
			teamId === awayTeam.id ? awayParticipatingPlayers : homeParticipatingPlayers;
		return ImportService.convertToCsvString(
			players.map(({ firstName, lastName, uniform, positions }) => ({
				Name: `${firstName} ${lastName}`,
				Uniform: uniform,
				Position: positions?.map(({ name }) => name).join(" / ")
			}))
		);
	};

	const saveDisabled = !isValid || isSubmitting;

	return (
		<form onSubmit={handleSubmit} className={commonClasses.form}>
			<Grid container spacing={2} className={classes.container}>
				<Grid item xs={12}>
					<Typography variant="h5">Event Wrap-Up</Typography>
				</Grid>
				{!isValid && (
					<Grid item xs={12}>
						<FormMessages messages={errors} />
					</Grid>
				)}
				<Grid item xs={12} sm={4} className={classes.timeGroup}>
					<ClockTimePicker
						value={values.endTime}
						handleTimeChange={setEndTime}
						label="End Time"
					/>
				</Grid>
				<Grid item xs={12} sm={8}>
					<TextField
						fullWidth
						variant="outlined"
						id="attendance"
						name="attendance"
						label="Attendance"
						value={values.attendance}
						error={touched.attendance && Boolean(errors.attendance)}
						helperText={touched.attendance && errors.attendance}
						onChange={handleChange}
						className={classes.attendance}
						type="number"
					/>
				</Grid>
				<Divider />
				<Grid item xs={12}>
					<Typography variant="h5">Finalize Records</Typography>
				</Grid>
				<TeamRecords
					team={awayTeam}
					handleChange={handleChange}
					values={values.records[awayTeam.id]}
					errors={getSafeOrDefault(() => errors.records[awayTeam.id], {})}
					touched={getSafeOrDefault(() => touched.records[awayTeam.id], {})}
				/>
				<TeamRecords
					team={homeTeam}
					handleChange={handleChange}
					values={values.records[homeTeam.id]}
					errors={getSafeOrDefault(() => errors.records[homeTeam.id], {})}
					touched={getSafeOrDefault(() => touched.records[homeTeam.id], {})}
				/>
				<Divider />
				<Grid item xs={12}>
					<Typography variant="h5">Notes</Typography>
				</Grid>
				<Grid item xs={12}>
					<TextField
						fullWidth
						variant="outlined"
						placeholder="Leave a note..."
						id="notes"
						name="notes"
						label="Notes"
						multiline
						minRows={2}
						value={values.notes}
						onChange={handleChange}
					/>
				</Grid>
				{participationStyle === ParticipationStyle.Continuous.code && (
					<>
						<Grid item xs={12}>
							<Typography variant="h5">Participation</Typography>
						</Grid>
						<Grid item xs={12} sm={6}>
							<ParticipantConfirmationList
								onModifyParticipants={onModifyParticipants}
								getCsvContent={getCsvContent}
								team={homeTeam}
								participants={homeParticipatingPlayers}
							/>
						</Grid>

						<Grid item xs={12} sm={6}>
							<ParticipantConfirmationList
								onModifyParticipants={onModifyParticipants}
								getCsvContent={getCsvContent}
								team={awayTeam}
								participants={awayParticipatingPlayers}
							/>
						</Grid>
					</>
				)}
			</Grid>

			<Toolbar className={commonClasses.actionToolbar}>
				<Grid container justifyContent="flex-end" spacing={1}>
					<Grid item>
						<SecondaryButton onClick={handleCancel} label="Cancel" />
					</Grid>
					<Grid item>
						<SecondaryButton
							onClick={handleSave}
							id="button-save"
							label="Save"
							disabled={saveDisabled}
						/>
					</Grid>
					<Grid item>
						<PrimaryButton
							id="button-finalize"
							label="Finalize"
							type="submit"
							disabled={saveDisabled}
						/>
					</Grid>
				</Grid>
			</Toolbar>
		</form>
	);
};

export default GameFinalize;
